import { isNullOrUndefined } from '../../../lib/typeGuards';
import { BlockType } from '../../../types/api/ElementType';
import { Icons } from '../../atoms/SVG';
import { focusNonFocusableElementAndToggleClassName } from '../../../lib/DomUtils/utils';
import {
  BlockWithAction,
  ContentWrapperAction,
  ContentWrapperAction as Action,
  ContentWrapperBlocks,
  ContentWrapperBlockTypes,
} from './ContentWrapper.types';
import { LayoutGroup } from '../LayoutGroup/LayoutGroup';

/** Returns the data-index attribute
 *
 * ONLY used in ContentWrapper
 */
export function getIndex(index?: number, baseIndex?: number, forHtml?: boolean): string;
export function getIndex(index?: undefined, baseIndex?: number, forHtml?: boolean): null;
export function getIndex(index?: number, baseIndex?: number, forHtml = false): string | null {
  // ...props.index is only defined inside a ContentWrapper
  if (isNullOrUndefined(index)) {
    // if that prop is null or undefined, we're not inside a ContentWrapper -> early exit
    return null;
  }

  // otherwise we have 2 options:
  // 2. baseIndex is undefined: child element of ContentWrapper
  // 2. baseIndex IS defined -> we are a child element of a LayoutGroup
  const indexString = isNullOrUndefined(baseIndex) ? `${index + 1}` : `${baseIndex + 1}-${index + 1}`;

  // dashed on HTML, dotted in UI
  return forHtml ? `block-${indexString}` : indexString.replace('-', '.');
}

export type Position = { position: number | string | null } | LayoutGroupPosition;

type LayoutGroupPosition = { positionImage: string | number; positionText: string | number };
/**
 * All buttons need positions except connect blocks.
 * */
export function getPosition(
  index: number,
  baseIndex: number | undefined,
  elementType: ContentWrapperBlockTypes,
  isInLayoutGroup: boolean | undefined,
  actionType: ContentWrapperAction.connectBlocks
): LayoutGroupPosition;
export function getPosition(
  index: number,
  baseIndex: number | undefined,
  elementType: string | undefined,
  isInLayoutGroup: boolean | undefined,
  actionType:
    | ContentWrapperAction.addBlock
    | ContentWrapperAction.deleteBlock
    | ContentWrapperAction.moveBlockDown
    | ContentWrapperAction.moveBlockUp
    | ContentWrapperAction.connectBlocks
    | undefined
): Position;
export function getPosition(
  index: number,
  baseIndex: number | undefined,
  elementType: string | undefined,
  isInLayoutGroup: boolean | undefined,
  actionType: string | undefined,
): Position | null {
  // Cases that should not happen
  if (
    (elementType === BlockType.layoutgroup && isInLayoutGroup) ||
    (actionType === Action.connectBlocks &&
      elementType !== BlockType.contentimage &&
      elementType !== BlockType.layoutgroup &&
      elementType !== BlockType.externalcontent)
  ) {
    return null;
  }
  // LayoutGroup need two positions.
  if (elementType === BlockType.layoutgroup && !isInLayoutGroup) {
    return {
      positionImage: getIndex(0, index, false),
      positionText: getIndex(1, index, false),
    };
    // Blocks in LayoutGroup component have different positioning.
  }
  if (isInLayoutGroup) {
    return {
      position: getIndex(index, baseIndex, false),
    };
    // ConnectBlock action of Image Block must know its position and position of following block
  }
  if (
    actionType === Action.connectBlocks &&
    (elementType === BlockType.contentimage || elementType === BlockType.externalcontent)
  ) {
    return {
      positionImage: index + 1,
      positionText: index + 2,
    };
  }
  return {
    position: getIndex(index, baseIndex, false),
  };
}

/**
 * Disabling ActionKeys.
 * Moving is not possible outside of array (highest, lowest position).
 * Same with delete.
 */
export function isActionDisabled(
  actionType: string,
  index: number,
  numBlocks: number,
  elementType?: string,
  isInLayoutGroup?: boolean,
) {
  switch (actionType) {
    case Action.moveBlockUp:
      if (elementType === BlockType.layoutgroup) {
        return index === 0;
      }
      return isInLayoutGroup || index === 0;
    case Action.moveBlockDown:
      if (elementType === BlockType.layoutgroup) {
        return index === numBlocks - 1;
      }
      return isInLayoutGroup || index === numBlocks - 1;
    case Action.connectBlocks:
      return false;
    case Action.deleteBlock:
      return !!isInLayoutGroup;
    default:
      return false;
  }
}

export const actionKeyToIcon = {
  [Action.connectBlocks]: Icons.CONNECT_BLOCKS,
  [Action.deleteBlock]: Icons.DELETE_TRASHCAN,
  [Action.moveBlockDown]: Icons.MOVE_DOWN,
  [Action.moveBlockUp]: Icons.MOVE_UP,
};

export function nextBlockIsRichtext(blocks: ContentWrapperBlocks[], index: number): boolean {
  return blocks[index + 1]?.elementType !== BlockType.richtext;
}

/** Return the first block from `blocks` which is not in `otherBlocks` */
export function notInOther(blocks: ContentWrapperBlocks[], otherBlocks: ContentWrapperBlocks[]) {
  return blocks.find(block => !otherBlocks.some(blk => blk.uuid === block.uuid));
}

/** Returns the index of a block in a given array or -1 otherwise */
export function indexIn(blocks: ContentWrapperBlocks[], block?: ContentWrapperBlocks) {
  return blocks.findIndex(blk => blk.uuid === block?.uuid);
}

export function isLayoutGroup(blockView: ContentWrapperBlocks): blockView is BlockWithAction<LayoutGroup> {
  return blockView.elementType === BlockType.layoutgroup;
}

/** Returns LayoutGroup blocks which groupLayout property has changed */
export function layoutGroupChanged(groups: ContentWrapperBlocks[], otherGroups: ContentWrapperBlocks[]) {
  return groups
    .filter(isLayoutGroup)
    .find(block =>
      otherGroups.filter(isLayoutGroup).some(blk => blk.groupLayout !== block.groupLayout && blk.uuid === block.uuid),
    );
}

/** Focus a block's headline based on the block's data-index attribute.
 * Extensively used in ContentWrapper to focus the headline but visually highlight the block instead.
 * Focus on headline keeps screen reader output reduced.
 * We set tabindex -1 to the headline, so we can focus it. We remove the tabindex later on blur.
 * At the same time, we set a class on the blockInteractions element to visually highlight the current position.
 * The blockInteractions element is scrolled smoothly into view to preserve cursor position, see SON-12585
 */
export const focusBlockHeadlineByIndex = (index: number) => {
  const elements = document.querySelectorAll<HTMLElement>(
    `[data-index="block-${index}"] h2.block-interactions__headline`,
  );
  const visualFocusElement = document.querySelectorAll<HTMLElement>(`[data-index="block-${index}"]`);
  if (elements.length === 1 && visualFocusElement) {
    focusNonFocusableElementAndToggleClassName(
      elements[0],
      visualFocusElement[0],
      'block-interactions--focus-on-headline',
    );
    visualFocusElement[0].scrollIntoView({ behavior: 'smooth' }); // see SON-12585
  }
};
